import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Amplify } from 'aws-amplify';
import { Authenticator, useAuthenticator } from '@aws-amplify/ui-react';
import '@aws-amplify/ui-react/styles.css';
import './styles/App.css';
import LandingPage from './components/LandingPage';
import awsConfig from './config/aws-exports';
import { Layout } from './shared/components/Layout';
import { Dashboard } from './features/dashboard';
import { JobList, JobDetail } from './features/jobs';
import { ManageEstimates, EstimateDetail, EstimateUploadWizard, NewEstimateWizard, EditEstimate } from './features/estimates';
import { Insights } from './features/insights';
import { AdminLayout, TenantList, TenantDetail, TenantUpload, CreateTenant, AdminPricingRules } from './features/admin';
import { PricingRules, Vocabulary, Integrations } from './features/settings';
import { useAuth } from './shared/hooks/useAuth';
import { TenantProvider, useTenant } from './shared/context/TenantContext';
import { ToastProvider } from './shared/context/ToastContext';
import { ThemeProvider } from './shared/context/ThemeContext';
import { ThemeToggle } from './shared/components/ThemeToggle';
import { ThemedLogin } from './shared/components/ThemedLogin';

Amplify.configure(awsConfig);

// Placeholder components for routes not yet implemented
function PlaceholderPage({ title }) {
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
      <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
      </svg>
      <h2 className="text-xl font-semibold text-gray-900 mb-2">{title}</h2>
      <p className="text-gray-500">This page is coming soon.</p>
    </div>
  );
}

// Protected admin route wrapper
function RequireSuperAdmin({ children }) {
  const { isSuperAdmin, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!isSuperAdmin) {
    return <Navigate to="/" replace />;
  }

  return children;
}

// Tenant access validation wrapper - ensures user belongs to the subdomain's tenant
function ValidateTenantAccess({ children }) {
  const { tenantId } = useAuth();
  const { tenant, isTenantPortal, isLoading: tenantLoading, error: tenantError } = useTenant();
  const { signOut } = useAuthenticator((context) => [context.signOut]);
  const [accessError, setAccessError] = useState(null);

  useEffect(() => {
    // If we're on a tenant subdomain and the user's tenantId doesn't match, sign them out
    if (isTenantPortal && tenant && tenantId && tenant.tenantId !== tenantId) {
      setAccessError("This account doesn't have access to this portal. Please contact your administrator.");
      signOut();
    }
  }, [isTenantPortal, tenant, tenantId, signOut]);

  if (tenantLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
      </div>
    );
  }

  if (accessError) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-100">
        <div className="bg-white p-8 rounded-lg shadow-lg max-w-md text-center">
          <svg className="w-16 h-16 text-red-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600 mb-4">{accessError}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-brand-primary text-white rounded-lg hover:opacity-90"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (tenantError) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-100">
        <div className="bg-white p-8 rounded-lg shadow-lg max-w-md text-center">
          <svg className="w-16 h-16 text-yellow-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Portal Not Found</h2>
          <p className="text-gray-600">{tenantError}</p>
        </div>
      </div>
    );
  }

  return children;
}

function AuthenticatedApp({ signOut }) {
  const { isAdmin } = useTenant();
  const { isSuperAdmin } = useAuth();

  return (
    <ValidateTenantAccess>
      <BrowserRouter>
        <Routes>
          {/* Admin Routes (Super Admin Only) - prioritized for admin subdomain */}
          <Route
            path="/admin/*"
            element={
              <RequireSuperAdmin>
                <AdminLayout onSignOut={signOut} />
              </RequireSuperAdmin>
            }
          >
            <Route index element={<TenantList />} />
            <Route path="tenants/new" element={<CreateTenant />} />
            <Route path="tenants/:id" element={<TenantDetail />} />
            <Route path="tenants/:id/upload" element={<TenantUpload />} />
            <Route path="pricing-rules" element={<AdminPricingRules />} />
          </Route>

          {/* Root redirect - send admin portal users to /admin */}
          {isAdmin && isSuperAdmin ? (
            <Route path="/" element={<Navigate to="/admin" replace />} />
          ) : (
            <Route path="/" element={<Layout onSignOut={signOut}><Dashboard /></Layout>} />
          )}

          {/* Client Routes */}
          <Route
            path="/*"
            element={
              <Layout onSignOut={signOut}>
                <Routes>
                  {/* Estimates - Unified estimate management */}
                  <Route path="/estimates" element={<ManageEstimates />} />
                  <Route path="/estimates/new" element={<NewEstimateWizard />} />
                  <Route path="/estimates/upload" element={<EstimateUploadWizard />} />
                  <Route path="/estimates/:estimateId" element={<EstimateDetail />} />
                  <Route path="/estimates/:estimateId/edit" element={<EditEstimate />} />

                  {/* Jobs/History - Legacy view */}
                  <Route path="/jobs" element={<JobList />} />
                  <Route path="/jobs/:id" element={<JobDetail />} />

                  {/* Insights (Pro) */}
                  <Route path="/insights" element={<Insights />} />

                  {/* Settings */}
                  <Route path="/settings/pricing" element={<PricingRules />} />
                  <Route path="/settings/vocabulary" element={<Vocabulary />} />
                  <Route path="/settings/integrations" element={<Integrations />} />
                  <Route path="/settings/industry" element={<PlaceholderPage title="Industry Settings" />} />
                  <Route path="/settings/team" element={<PlaceholderPage title="Team Members" />} />

                  {/* Catch-all redirect to dashboard */}
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </Layout>
            }
          />
        </Routes>
      </BrowserRouter>
    </ValidateTenantAccess>
  );
}

function AppContent() {
  const { user, signOut } = useAuthenticator((context) => [context.user]);
  const { isTenantPortal, isAdmin, isLoading: tenantLoading, error: tenantError } = useTenant();
  const [showAuth, setShowAuth] = useState(false);

  // On a tenant subdomain, always show the login (no landing page)
  // On admin subdomain, always show the login
  // On main domain, show landing page with option to login

  if (tenantLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
      </div>
    );
  }

  if (user) {
    return <AuthenticatedApp signOut={signOut} />;
  }

  // Floating toggle for unauthenticated screens (landing, login, errors)
  const floatingToggle = (
    <div className="fixed top-4 right-4 z-50 rounded-full bg-white/80 dark:bg-gray-900/80 backdrop-blur shadow border border-gray-200 dark:border-gray-700">
      <ThemeToggle />
    </div>
  );

  // Tenant subdomain error (subdomain not found)
  if (isTenantPortal === false && tenantError) {
    return (
      <>
        {floatingToggle}
        <div className="flex items-center justify-center h-screen bg-gray-100 dark:bg-gray-950">
          <div className="bg-white dark:bg-gray-900 p-8 rounded-lg shadow-lg max-w-md text-center">
            <svg className="w-16 h-16 text-yellow-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">Portal Not Found</h2>
            <p className="text-gray-600 dark:text-gray-400">{tenantError}</p>
          </div>
        </div>
      </>
    );
  }

  // On tenant or admin subdomain - show themed login directly
  if (isTenantPortal || isAdmin) {
    return <>{floatingToggle}<ThemedLogin /></>;
  }

  // Show auth flow if user clicked login
  if (showAuth) {
    return <>{floatingToggle}<ThemedLogin onBack={() => setShowAuth(false)} /></>;
  }

  // Main domain - show landing page
  return <>{floatingToggle}<LandingPage onLogin={() => setShowAuth(true)} /></>;
}

function App() {
  return (
    <ThemeProvider>
      <Authenticator.Provider>
        <TenantProvider>
          <ToastProvider>
            <AppContent />
          </ToastProvider>
        </TenantProvider>
      </Authenticator.Provider>
    </ThemeProvider>
  );
}

export default App;
